/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useOrgData } from "state/OrgDataContext";
import { Heading, Stack, Text, FontAwesomeIcon } from "components";
import { Input } from "components/Input";
import Checkbox from "components/Input/Checkbox";
import { invitationDetailTitle } from "pages/On-boarding/RSVP/RSVP";
import getFormattedDate from "utils/getFormattedDate";
import { useTranslation } from "react-i18next";

const AvailabilitySection = ({
  level = 2,
  setInvitationDetails,
  invitationDetails,
  dateOptionsErrors,
}) => {
  return (
    <Stack className=" gap-y-6">
      {+level === 1 ? (
        <Level1
          setInvitationDetails={setInvitationDetails}
          invitationDetails={invitationDetails}
          dateOptionsErrors={dateOptionsErrors}
        />
      ) : null}
      {+level === 2 ? (
        <Level2
          setInvitationDetails={setInvitationDetails}
          invitationDetails={invitationDetails}
        />
      ) : null}
      {+level === 3 ? <Level3 invitationDetails={invitationDetails} /> : null}
    </Stack>
  );
};

const AvailableDate = ({
  start,
  end,
  disabled = false,
  selected,
  onChange,
  id = "",
}) => {
  return (
    <label
      htmlFor={id}
      className=" -text--theme-colors-text-body flex gap-x-1.5 items-center"
    >
      {!disabled && (
        <Checkbox
          size="sm"
          type="checkbox"
          value={selected}
          onChange={onChange}
          id={id}
        />
      )}
      <div className="-text--theme-colors-icon-primary px-1">
        <FontAwesomeIcon size="xl" name="fa-calendar-day" />
      </div>
      <span>{getFormattedDate(start)}</span>
      <span> - </span>
      <span>{getFormattedDate(end)}</span>
    </label>
  );
};

const Level1 = ({
  setInvitationDetails,
  invitationDetails,
  dateOptionsErrors,
}) => {
  const { t } = useTranslation();
  const handleOnDateChange = (value, name, i) => {
    setInvitationDetails((prev) => {
      return {
        ...prev,
        selected_dates: {
          ...prev.selected_dates,
          [invitationDetailTitle(i + 1)]: {
            ...prev.selected_dates[invitationDetailTitle(i + 1)],
            [name]: value,
          },
        },
      };
    });
  };
  return (
    <Stack className="gap-y-6">
      <Stack>
        <Heading className="-text--theme-colors-text-title-brand" as="h3">
          {t("availabilitySection.title")}
        </Heading>
        <Text className="-text--theme-colors-text-body">
          {t("availabilitySection.availabilityMSG")}
        </Text>
      </Stack>
      <Stack className="gap-y-8">
        {invitationDetails.dates_number &&
          Array.from({ length: invitationDetails.dates_number }).map((_, i) => {
            const title = invitationDetailTitle(i + 1);
            const hasError = dateOptionsErrors[title];
            return (
              <Stack key={i} className="gap-y-2.5">
                <Text className="-text--theme-colors-text-subtitle-brand text-sm font-bold">
                  {title}
                </Text>
                <div className="grid md:grid-cols-2 gap-x-4 gap-y-3">
                  <Input
                    type="date"
                    label={t("common.labels.from")}
                    required={i === 0}
                    onChange={(e) =>
                      handleOnDateChange(e.target.value, "start", i)
                    }
                    value={
                      invitationDetails.selected_dates[
                        invitationDetailTitle(i + 1)
                      ]?.start
                    }
                    error={hasError}
                  />
                  <Input
                    type="date"
                    label={t("common.labels.to")}
                    required={i === 0}
                    onChange={(e) =>
                      handleOnDateChange(e.target.value, "end", i)
                    }
                    value={
                      invitationDetails.selected_dates[
                        invitationDetailTitle(i + 1)
                      ]?.end
                    }
                    error={hasError}
                  />
                </div>
              </Stack>
            );
          })}
      </Stack>
    </Stack>
  );
};

const Level2 = ({ setInvitationDetails, invitationDetails }) => {
  const { t } = useTranslation();
  const { dataContent: EVENT_CONTENT } = useOrgData();
  const suggestedDates = invitationDetails.suggested_dates
    ? Object.entries(invitationDetails.suggested_dates)
    : [];
  return (
    <Stack className="gap-y-3">
      <Stack>
        <Heading className="-text--theme-colors-text-title-brand" as="h3">
          {EVENT_CONTENT?.RSVP?.tripDatesLabel ??
            t("availabilitySection.tripDates")}
        </Heading>
        <Text className="-text--theme-colors-text-body">
          {t("availabilitySection.indicateYourAvailability")}
        </Text>
      </Stack>
      <div className="grid  w-full gap-y-4">
        {suggestedDates &&
          suggestedDates.length > 0 &&
          suggestedDates.map(([key, date], i) => {
            const [start, end] = date.split(",");
            return (
              <AvailableDate
                key={i}
                onChange={(e) => {
                  const isAvailable = e.target.checked;
                  setInvitationDetails((prev) => {
                    const newDetails = { ...prev };
                    if (isAvailable) {
                      newDetails.selected_dates[key] = { start, end };
                    } else {
                      delete newDetails.selected_dates[key];
                    }
                    return newDetails;
                  });
                }}
                id={start + end + i}
                start={start}
                end={end}
              />
            );
          })}
      </div>
    </Stack>
  );
};

const Level3 = ({ invitationDetails }) => {
  const { t } = useTranslation();
  const { dataContent: EVENT_CONTENT } = useOrgData();
  return (
    <Stack className="gap-y-3">
      <Heading className="-text--theme-colors-text-title-brand" as="h3">
        {EVENT_CONTENT?.RSVP?.tripDatesLabel ??
          t("availabilitySection.tripDates")}
      </Heading>
      <Stack>
        {invitationDetails.suggested_dates &&
          invitationDetails.suggested_dates.length &&
          invitationDetails.suggested_dates.map((date, i) => {
            const [start, end] = date.split(",");
            return <AvailableDate start={start} end={end} disabled={true} />;
          })}
      </Stack>
    </Stack>
  );
};

export default AvailabilitySection;
